import { useEffect, useState } from "react";
import { subscribeContactSignal } from "./contactSignals";
import type { ContactSignalState } from "./contactSignals";

const IDLE: ContactSignalState = { signal: null, ready: false, error: null };

/**
 * The contact's SIGNAL — `{ v, at, deleted }` from `contact-signals/{contactId}` under the
 * book, or null while no signal document exists. One Firestore listener per (book, contact)
 * however many mounts ask (`contactSignals.ts`), opened while `enabled` and closed when the
 * last mount lets go.
 *
 * This is the "something changed" half of the real-time layer without sockets; the "what
 * changed" half is `useContactDocument`, which refetches when `v` passes the version it
 * holds. Read this hook directly only to observe; a screen that wants the contact wants
 * `useContactDocument` / `useContactByPhone`.
 *
 * The answer belongs to the (book, contact) it was asked about: a render that asks about a
 * different one gets the idle state in that very render, never the previous contact's signal.
 */
export default function useContactSignal(
  bookKey: string | null,
  contactId: string | null,
  enabled = true
): ContactSignalState {
  const key = enabled && bookKey && contactId ? `${bookKey}\n${contactId}` : null;
  const [answer, setAnswer] = useState<{ key: string; state: ContactSignalState } | null>(null);

  useEffect(() => {
    if (!key || !bookKey || !contactId) return undefined;
    return subscribeContactSignal(bookKey, contactId, (state) => setAnswer({ key, state }));
  }, [key, bookKey, contactId]);

  return key && answer?.key === key ? answer.state : IDLE;
}
